/**
 * Helyzet-diagnosztika — MI TÖRTÉNIK MOST a helymeghatározással.
 *
 * ⚠️ EZÉRT VAN: iOS-en a helyzet-engedély élesben megbízhatatlan, és eddig
 * nem volt mivel megnézni, miért. A WebView `navigator.permissions` API-ja a
 * helyzetről semmit nem mond (a `bugReport.ts` ezért írt mindig
 * `ismeretlen`-t), Windowsról pedig nincs Safari Web Inspector, amivel a
 * futó WebView-ba bele lehetne nézni. Ez a modul azt a rést zárja be: a
 * natív oldal állapota ÉS a JS-oldali kísérletek naplója egy helyen,
 * készüléken, működés közben olvashatóan.
 *
 * ⚠️ SEMMI, AMI HELYADAT. Állapotnév, darabszám, időtartam igen —
 * koordináta, pontosság és cellaazonosító SOHA. Ugyanaz a szabály, mint a
 * morzsanaplónál (`src/lib/breadcrumbs.ts`) és a hibabejelentésnél
 * (`src/lib/bugReport.ts`): ez az adat a bejelentéssel együtt a szerverre
 * kerül.
 *
 * ⚠️ A KÍSÉRLETNAPLÓ MINDIG GYŰLIK, debug mód nélkül is. Nem a morzsanapló
 * mintája ez, és szándékosan: egy tesztelő akkor kapcsol debug módba, amikor
 * a hiba MÁR megtörtént, és épp az érdekelne, mi volt előtte. Tizenkét
 * bejegyzés két számmal és egy rövid szöveggel — a költsége mérhetetlen.
 */

import { isNativeApp } from '@/lib/platform';
import { addBreadcrumb } from '@/lib/breadcrumbs';
import { nativeLocationDiagnostics, type NativeLocationDiagnostics } from '@/tracking/nativeSource';

export type { NativeLocationDiagnostics };

/** Egy helyzetlekérdezés a `currentPosition()`-ön át. */
export interface PositionAttempt {
  /** Unix ms — mikor INDULT a kísérlet. */
  at: number;
  source: 'native' | 'web';
  /** Miért kértünk helyzetet — a hívó adja meg (pl. `térkép`, `időjárás`). */
  purpose: string;
  outcome: 'pending' | 'ok' | 'error';
  /** Mennyi ideig tartott. Amíg fut, hiányzik. */
  durationMs?: number;
  /** A hiba szövege, ha volt. Koordinátát sosem tartalmaz. */
  error?: string;
}

/**
 * Ennyi kísérletet tartunk. Egy képernyőnyi panelbe ennél több úgysem fér, és
 * a hiba mindig az utolsó néhányban van.
 */
export const ATTEMPT_CAPACITY = 12;

const attempts: PositionAttempt[] = [];
let nextHandle = 1;
/** A futó kísérletek a kiadott azonosító szerint — a befejezéshez kell. */
const running = new Map<number, PositionAttempt>();

/**
 * Új kísérlet a naplóba. A visszaadott azonosítót a `endPositionAttempt`
 * várja.
 */
export function beginPositionAttempt(source: 'native' | 'web', purpose: string): number {
  const attempt: PositionAttempt = { at: Date.now(), source, purpose, outcome: 'pending' };
  attempts.push(attempt);
  if (attempts.length > ATTEMPT_CAPACITY) attempts.shift();
  const handle = nextHandle++;
  running.set(handle, attempt);
  addBreadcrumb('info', `helyzet: kérés (${source}, ${purpose})`);
  return handle;
}

/** A kísérlet lezárása. `error` nélkül sikeres. */
export function endPositionAttempt(handle: number, error?: unknown): void {
  const attempt = running.get(handle);
  if (!attempt) return;
  running.delete(handle);
  attempt.durationMs = Date.now() - attempt.at;
  if (error === undefined) {
    attempt.outcome = 'ok';
    addBreadcrumb('info', `helyzet: megvan (${attempt.durationMs} ms)`);
    return;
  }
  attempt.outcome = 'error';
  attempt.error = error instanceof Error ? error.message : String(error);
  addBreadcrumb('error', `helyzet: hiba (${attempt.durationMs} ms) — ${attempt.error}`);
}

/** A napló másolata, legrégebbitől a legújabbig. */
export function readPositionAttempts(): PositionAttempt[] {
  return attempts.map((attempt) => ({ ...attempt }));
}

/** Csak a tesztekhez — éles kódból ne hívd. */
export function resetPositionAttempts(): void {
  attempts.length = 0;
  running.clear();
}

export interface LocationDiagnostics {
  /** Nyugtázta-e a felhasználó a magyarázó képernyőt ezen az eszközön. */
  primerSeen: boolean;
  /** A natív oldal állapota, ha natív appban futunk és a plugin válaszolt. */
  native: NativeLocationDiagnostics | null;
  /** Miért nem jött natív állapot. Böngészőben `nem natív`. */
  nativeError: string | null;
  /** Amit a böngésző mond — iOS WebView-ban rendszerint semmit. */
  webPermission: string;
  attempts: PositionAttempt[];
}

/**
 * A magyarázó képernyő nyugtázásának kulcsa.
 *
 * ⚠️ ITT lakik, nem a komponensben: a `LocationPrimer` innen olvassa
 * (`src/components/LocationPrimer.tsx`). Két helyen leírva a kulcs előbb-utóbb
 * elcsúszik, és akkor a diagnosztika mást mond, mint amit a felhasználó lát.
 */
export const LOCATION_PRIMER_KEY = 'grundo.locationPrimerSeen';

function readPrimerSeen(): boolean {
  try {
    return localStorage.getItem(LOCATION_PRIMER_KEY) === '1';
  } catch {
    /*
     * Itt a `false` a helyes: a kérdés nem az, hogy MUTASSUK-E a magyarázatot
     * (arra a komponens a nem-zaklatás felé téved), hanem hogy nyugtázta-e a
     * felhasználó. Ha nem tudjuk, ne állítsuk, hogy igen.
     */
    return false;
  }
}

/**
 * A böngésző engedélyállapota, ha elárulja.
 *
 * ⚠️ NEM HIBA, ha `ismeretlen` marad: a WKWebView `navigator.permissions`
 * API-ja a `geolocation` nevet nem ismeri. Pont ez az oka, hogy a natív
 * állapotra szükség van — az `ismeretlen` itt MÉRÉS, nem hiányosság.
 */
async function readWebPermission(): Promise<string> {
  try {
    const status = await navigator.permissions?.query({ name: 'geolocation' as PermissionName });
    return status?.state ?? 'ismeretlen';
  } catch {
    return 'ismeretlen';
  }
}

/** A teljes kép — ezt rajzolja a tesztelői panel, és ezt viszi a bugreport. */
export async function readLocationDiagnostics(): Promise<LocationDiagnostics> {
  const [webPermission, nativeResult] = await Promise.all([
    readWebPermission(),
    readNative(),
  ]);
  return {
    primerSeen: readPrimerSeen(),
    native: nativeResult.value,
    nativeError: nativeResult.error,
    webPermission,
    attempts: readPositionAttempts(),
  };
}

async function readNative(): Promise<{ value: NativeLocationDiagnostics | null; error: string | null }> {
  if (!isNativeApp()) return { value: null, error: 'nem natív' };
  try {
    return { value: await nativeLocationDiagnostics(), error: null };
  } catch (error) {
    /*
     * A régi natív buildekben a metódus még nincs meg — a Capacitor ilyenkor
     * „not implemented"-tel utasítja el. Ez önmagában diagnózis: a WebView
     * újabb, mint a köré épült natív réteg.
     */
    return { value: null, error: error instanceof Error ? error.message : String(error) };
  }
}
